/**
 * A grouping of `query-event-selector`s with support for adding filters, clone/remove, and drag/drop.
 */

import capitalize from 'lodash/capitalize';
import cloneDeep from 'lodash/cloneDeep';
import findKey from 'lodash/findKey';
import get from 'lodash/get';
import isObject from 'lodash/isObject';
import mapValues from 'lodash/mapValues';
import omit from 'lodash/omit';
import pickBy from 'lodash/pickBy';
import debounce from 'lodash/debounce';

import {Component} from 'panel';
import {
  ALL_MATH_TYPES,
  EVENT_MATH_LABELS,
  PROPERTY_MATH_LABELS,
  MATH_TYPE,
  PRIMATIVE_MATH_TYPES,
  XAU_MATH_TYPES,
} from 'common/report/constants';
import {DisplayContext} from 'common/widgets/date-range-picker/models/types';

import {getAlphaLabelFromIndex, serializeAttributes} from '../../util';
import {insertAtIndex, removeByIndex, replaceByIndex} from '../../util/array';
import {NAVIGATION_KEY_CODES, emitTrackEvent, propagateEvent, preventTracking} from '../../util/dom';
import {registerMPElement} from '../../util/register-element';
import {ResourceType, PropertyType} from '../property-filter-menu/models/types';
import {PropertyItem} from '../query-block/properties-menu/types';
import {
  EventEntryPlaceholder,
  isInlineCohortItem,
  isPropertySelectorType,
  QueryEntry,
  QueryEventEntry,
  SelectorType,
  SelectorTypeDefinitions,
} from '../query-event-selector/types';
import {RangeSelection} from '../step-range-selector';

import {ResourceTrackingEvent, ResourceTrackingTypes, TrackingEvent} from './tracking';
import {
  Action,
  EntryType,
  RowLabelType,
  RowTypeOption,
  RowTypeComplexOption,
  SelectorTypesLabels,
  SelectorTypeMathFunctions,
  EMPTY_EVENT_ENTRY,
  EMPTY_EXCLUSION_EVENT_ENTRY,
} from './types';
import {ControlBarAction, ControlBarActionItems} from './constants';

import template from './index.jade';
import css from './index.styl?inline';
import {EventItem} from '../query-block/events-menu/types';
import {findKeyByValue} from 'common/util/typescript';
import DateRange from '../date-range-picker/models/date-range';

enum DROP_POSITION {
  ABOVE,
  BELOW,
}

interface StepRenameModal {
  step?: number;
  text: string;
}

enum ActiveEventPart {
  Type,
  Value,
  NumericProperty,
  Aggregator,
  Filter,
}

interface QueryEventsSectionState {
  activeEvent: number;
  activeEventPart: ActiveEventPart;
  draggingOverStepIndex: number;
  draggingGripperIndex: number;
  dropPosition: DROP_POSITION;
  stepRenameModal: StepRenameModal;
  stagedType: SelectorType;
  placeholders: Map<number, EventEntryPlaceholder>;
}

const SelectorTypesIcons = {
  [ResourceType.Cohort]: `community`,
  [ResourceType.Event]: `event`,
  [ResourceType.User]: `profile`,
  [SelectorTypeDefinitions.EventOrUserProperty]: `event-attribute`,
  [SelectorTypeDefinitions.UserProperty]: `profile-attribute`,
  [SelectorTypeDefinitions.Behavior]: `event`,
  [SelectorTypeDefinitions.EventLaunch]: `event`,
};

// Defines types of entities that can be added to the list
type TypeList = {[key in SelectorType]?: RowTypeComplexOption};

const LIST_TYPES: TypeList = {
  [ResourceType.Cohort]: {
    label: capitalize(`${SelectorTypesLabels[ResourceType.Cohort]}`),
    value: ResourceType.Cohort,
    icon: `community`,
  },
  [ResourceType.Event]: {
    label: capitalize(`${SelectorTypesLabels[ResourceType.Event]}`),
    value: ResourceType.Event,
    icon: `event`,
  },
  [ResourceType.User]: {
    label: `Profile`,
    value: ResourceType.User,
    icon: `profile`,
  },
  [SelectorTypeDefinitions.EventOrUserProperty]: {
    label: capitalize(`${SelectorTypesLabels[SelectorTypeDefinitions.EventOrUserProperty]}`),
    value: SelectorTypeDefinitions.EventOrUserProperty,
    icon: SelectorTypesIcons[SelectorTypeDefinitions.EventOrUserProperty],
  },
  [SelectorTypeDefinitions.UserProperty]: {
    label: capitalize(`${SelectorTypesLabels[SelectorTypeDefinitions.UserProperty]}`),
    value: SelectorTypeDefinitions.UserProperty,
    icon: SelectorTypesIcons[SelectorTypeDefinitions.UserProperty],
  },
  [SelectorTypeDefinitions.Behavior]: {
    label: capitalize(`${SelectorTypesLabels[SelectorTypeDefinitions.Behavior]}`),
    value: SelectorTypeDefinitions.Behavior,
    icon: SelectorTypesIcons[SelectorTypeDefinitions.Behavior],
  },
  [SelectorTypeDefinitions.EventLaunch]: {
    label: capitalize(`${SelectorTypesLabels[SelectorTypeDefinitions.EventLaunch]}`),
    value: SelectorTypeDefinitions.EventLaunch,
    icon: SelectorTypesIcons[SelectorTypeDefinitions.EventLaunch],
  },
};

// TODO rename to e.g. mp-query-block-section now that this encompasses more than events
export default registerMPElement(
  `mp-query-events-section`,
  class QueryEventsSection extends Component<QueryEventsSectionState> {
    static get attrsSchema() {
      return {
        ...super.attrsSchema,
        'add-entry-label': `string`,
        'add-entry-menu-width': {type: `number`, default: 110},
        'can-rename-step': `boolean`,
        'data-group-id': `string`,
        'data-groups-enabled': `boolean`,
        'date-range': `string`,
        'disable-clone-event': {type: `boolean`, default: false},
        'disable-delete-event': {type: `boolean`, default: false},
        'enable-behavioral-properties': {type: `boolean`, default: false},
        'entry-type': {type: `string`, default: EntryType.Event, enum: Object.values(EntryType)},
        'event-aggregator-functions': {
          type: `json`,
          description: `list of event aggregator function choices from primative math types`,
        },
        'property-aggregator-functions': {
          type: `json`,
          description: `list of property aggregator function choices from primative math types`,
        },
        'event-entries': {
          type: `json`,
          default: [],
          description: `interface: EventEntry[]`,
        },
        'exclude-autotrack-events': {
          type: `boolean`,
          default: false,
          description: `whether to hide autotrack events from the events menu`,
        },
        'exclude-close-selectors': {type: `json`, default: []},
        'include-custom-properties': {
          type: `string`,
          enum: [`none`, `persisted-only`, `all`],
          default: `none`,
          description: `whether to hide custom properties from properties menus`,
        },
        'is-blocked-people': `boolean`,
        'max-data-history-days': {
          type: `number`,
          description: `max days time menu can query into data history`,
        },
        'metrics-mode': {type: `boolean`},
        'num-exclusion-steps': `number`,
        'project-has-people': `boolean`,
        'project-id': `number`,
        'user-id': `number`,
        'row-draggable': {
          type: `boolean`,
          default: false,
          description: `Whether the events can be dragged to rearrange`,
        },
        'row-types': {
          type: `json`,
          default: [ResourceType.Event],
          description: `Default row types. Interface: RowTypeOption[]`,
        },
        'row-index-toggleable': {type: `boolean`, default: false, description: `Whether the row's label is toggleable`},
        'row-index-toggle-label': `string`,
        'row-label-type': {type: `string`, default: RowLabelType.Numeric, enum: Object.values(RowLabelType)},
        'row-limit': {type: `number`},
        'show-all-events': `boolean`,
        'show-any-event': `boolean`,
        'hide-edit-controls': {
          type: `boolean`,
          description: `show controls that popup modals to edit entities like custom events and properties`,
        },
        'show-sessions': `boolean`,
        'show-top-events': `boolean`,
        'show-numeric-aggregations': `boolean`,
        'show-numeric-properties': `boolean`,
        'show-special-date-property': {
          type: `boolean`,
          description: `enables special date property and breakdown type for datetime`,
          default: false,
        },
        'show-special-date-property-unit': {
          type: `boolean`,
          description: `enables the unit selection for special date property break-down`,
          default: true,
        },
        'support-b2b-cohorts': `boolean`,
        'support-inline-cohorts': `boolean`,
        'time-display-context': {type: `string`, default: DisplayContext.FullLength},
        'track-properties': {
          type: `json`,
          default: {},
          description: `If specified, adds extra properties to the track event. These will override ones from query-builder`,
        },
        'workspace-id': `number`,
      };
    }

    get config() {
      return {
        css,
        template,
        useShadowDom: true,

        defaultState: {
          activeEvent: null,
          activeEventPart: null,
          draggingGripperIndex: null,
          draggingOverStepIndex: null,
          dropPosition: null,
          stagedType: null,
          placeholders: new Map(),
          stepRenameModal: null,
        },

        helpers: {
          Action,
          ActiveEventPart,
          EntryType,
          isPropertySelectorType,
          ResourceType,
          RowLabelType,
          serializeAttributes,
          SelectorTypeDefinitions,
          EventEntryPlaceholder,

          canDeleteEntry: () => this.attr(`disable-delete-event`) === false && this.attr(`row-limit`) !== 1,
          getSelectedTypeLabel: (entryType) => SelectorTypesLabels[entryType],
          isExclusionStepsSection: () => this.attr(`entry-type`) === EntryType.ExclusionEvent,
          isDroppingAbove: (index) =>
            this.state.draggingOverStepIndex === index && this.state.dropPosition === DROP_POSITION.ABOVE,
          isDroppingBelow: (index) =>
            this.state.draggingOverStepIndex === index && this.state.dropPosition === DROP_POSITION.BELOW,
          preventTracking: (ev: Event): void => preventTracking(ev),
          propagateEvent: (ev) => propagateEvent(this, ev),

          onControlSelect: (value, index) => {
            switch (value) {
              case ControlBarAction.ApplyProperty:
                return this.helpers.showPlaceholderForEntry(EventEntryPlaceholder.Property, index);

              case ControlBarAction.RemoveProperty:
                return this.helpers.removeNumericProperty(index);

              case ControlBarAction.Filter:
                return this.helpers.showPlaceholderForEntry(EventEntryPlaceholder.Filter, index);

              case ControlBarAction.Rename:
                return this.helpers.stepRenameModal.onTriggered(index);

              case ControlBarAction.Duplicate:
                return this.helpers.onEventActionClick({action: Action.Clone, index});

              case ControlBarAction.Delete:
                return this.helpers.onEventActionClick({action: Action.Remove, index});
            }
          },

          getLineActionControls: (index, entry, isPlaceholder) => {
            const isCohortEntry = entry.type === ResourceType.Cohort;
            const isPropertyEntry = isPropertySelectorType(entry.type);
            const isBehaviorEntry = entry.type === SelectorTypeDefinitions.Behavior;
            const canUseProperty =
              !isPlaceholder &&
              this.attr(`show-numeric-properties`) &&
              !isCohortEntry &&
              !isPropertyEntry &&
              !isBehaviorEntry;
            const canApplyProperty = canUseProperty && !this.helpers.getSelectedNumericProperty(index);
            const canRemoveProperty = canUseProperty && this.helpers.getSelectedNumericProperty(index);
            const canApplyFilter = !isPlaceholder && !isCohortEntry && !isPropertyEntry;
            const canRenameStep = !isPlaceholder && this.attr(`can-rename-step`);
            const canDeleteEntry = this.helpers.canDeleteEntry();
            const canDuplicate = !isPlaceholder && !this.attr(`disable-clone-event`) && this.helpers.canAddRow();

            return [
              {
                items: [
                  canApplyFilter && ControlBarActionItems[ControlBarAction.Filter],
                  canApplyProperty && ControlBarActionItems[ControlBarAction.ApplyProperty],
                  canRemoveProperty && ControlBarActionItems[ControlBarAction.RemoveProperty],
                  canRenameStep && ControlBarActionItems[ControlBarAction.Rename],
                  canDuplicate && ControlBarActionItems[ControlBarAction.Duplicate],
                  canDeleteEntry && ControlBarActionItems[ControlBarAction.Delete],
                ].filter(Boolean),
              },
            ];
          },

          /** Disable the adding button when row-limit is 1, the placeholder row is added */
          canAddRow: () => {
            const rowLimit = this.attr(`row-limit`);

            if (rowLimit === 1) {
              return false;
            }

            return rowLimit > 0 ? this.helpers.getEventEntries().length < rowLimit : true;
          },

          /** Aborts staging new row when delete action is performed */
          onEventActionClick: ({action, index}: {action: Action; index: number}): void => {
            const entry = this.helpers.getEventEntries()[index];

            if (entry.event.value === null && action === Action.Remove) {
              this.abortRowStage();
            } else {
              this.updateEventEntries(action, {index});
            }
          },

          /** Disables adding until last row is commited */
          canAddNewItem: () => !this.state.stagedType,

          /** Defines the main item that can be added to the list */
          getMainActionItem: () => Object.values(this.getListTypeItems())[0],

          /** Defines entity actions that can be added to the list */
          getEntityAddActions: () => ({
            actions: Object.values(this.getListTypeItems()),
            sections: [
              {
                items: Object.values(this.getListTypeItems()),
              },
            ],
          }),

          getSelectedTypeIcon: (entry: QueryEntry) => {
            return this.getSelectorTypesIcon(entry);
          },

          /** Specifies if an entry has numeric properties available for selection */
          hasNumericProperty: (index) => {
            const entry = this.helpers.getEventEntries()[index];

            return (
              entry.type !== ResourceType.Cohort &&
              this.attr(`show-numeric-properties`) &&
              entry.event.value &&
              !this.helpers.isXAUSelected(index)
            );
          },

          getRowLabel: (index: number): string => {
            const entries = this.helpers.getEventEntries();

            // only toggle if label is provided and event is set as toggled.
            if (this.attr(`row-index-toggle-label`) && entries[index].eventIndexLabelToggled) {
              return this.attr(`row-index-toggle-label`);
            }

            switch (this.attr(`row-label-type`)) {
              case RowLabelType.Alpha:
                return getAlphaLabelFromIndex(index);
              case RowLabelType.Numeric:
                return (index + 1).toString();
              default:
                return `-`;
            }
          },

          showPlaceholderForEntry: (type: EventEntryPlaceholder, index: number) => {
            const {placeholders} = this.state;
            placeholders.set(index, type);

            this.update({placeholders});
          },

          updateShowClause: (ev, index: number): void => {
            const eventStep = this.getEventEntries()[index];
            const aggregationOperator = ev.detail.item.value as MATH_TYPE;
            const entry: QueryEntry = {...eventStep, aggregationOperator};

            this.updateEventEntries(Action.Edit, {entry, index});
            this.dispatchTrack(TrackingEvent.SelectNumericAggregator, {
              Aggregator: aggregationOperator,
              'Event index': index,
              'Event name': entry.event.label,
            });
          },

          applyNumericProperty: (item, index: number): void => {
            if (!item) {
              this.helpers.removeNumericProperty(index);
              return;
            }
            const sourceEntry = this.getEventEntries()[index];
            let entry: QueryEntry = {
              ...sourceEntry,
              property: item,
            };

            const entryAggregatorFunctions = this.getAggregatorFunctionsForEntry(entry);
            const validPropertyAggregator = entryAggregatorFunctions.some((section) =>
              section.items.find((item) => item.value === sourceEntry.aggregationOperator),
            );
            const aggregationOperator = validPropertyAggregator
              ? entry.aggregationOperator
              : this.getEntryDefaultOperator(entry.type);

            entry = {
              ...entry,
              aggregationOperator,
            };

            this.updateEventEntries(Action.Edit, {entry, index});
            this.dispatchTrack(TrackingEvent.SelectNumericProperty, {
              'Event index': index,
              'Event name': entry.event.label,
              'Property name': entry.property.label,
              'Property data type': entry.property.type,
            });
          },

          removeNumericProperty: (index: number): void => {
            let entry: QueryEntry = this.getEventEntries()[index];
            entry = {
              ...entry,
              aggregationOperator: this.getEntryDefaultOperator(entry.type),
              property: null,
            };

            this.updateEventEntries(Action.Edit, {entry, index});
            this.dispatchTrack(TrackingEvent.ClearNumericProperty, {
              'Event index': index,
              'Event name': entry.event.label,
            });
          },

          isXAUSelected: (index: number): boolean => {
            if (!this.attr(`show-numeric-aggregations`)) {
              return false;
            }

            const eventStep = this.getEventEntries()[index];

            return eventStep && !!findKeyByValue(XAU_MATH_TYPES, eventStep.aggregationOperator);
          },

          getSelectedNumericProperty: (index: number): PropertyItem => {
            const eventStep = this.getEventEntries()[index];

            return eventStep && eventStep.property;
          },

          getEventEntries: (): QueryEntry[] => this.getEventEntries({appendPlaceholder: true}),

          setActiveEvent: (state, index: number, part: ActiveEventPart): void => {
            const {activeEvent, activeEventPart} = this.state;

            if (state === `close`) {
              this.update({placeholders: new Map()});
            }

            if (state === `open`) {
              this.update({
                activeEvent: index,
                activeEventPart: part,
              });
            } else if (activeEvent === index && activeEventPart === part) {
              if (state === `close`) {
                const entry = this.getEventEntries()[index];
                if (!entry) {
                  this.abortRowStage();
                }
              } else {
                this.update({
                  activeEvent: null,
                  activeEventPart: null,
                });
              }
            }
          },

          onSelectMenuChange: (ev, index: number, part: ActiveEventPart) => {
            const {isOpen} = ev.detail;
            const status = isOpen ? `open` : `close`;

            this.helpers.setActiveEvent(status, index, part);
          },

          /** Changes type of staged entry */
          onAddTypeSelected: (item: RowTypeComplexOption) => {
            const index = this.helpers.getEventEntries().length;

            // checks for static defined row types and
            // append that to the list
            const rowTypes = this.getListTypeItems();
            const typeDefinition = rowTypes[item.value];

            if (typeDefinition.readOnlyEvent) {
              const entry = this.getNewEntry(item.value, typeDefinition.readOnlyEvent);
              this.updateEventEntries(Action.Add, {entry, index});
            } else {
              this.update({
                stagedType: item.value,
                activeEvent: index,
                activeEventPart: ActiveEventPart.Value,
              });
            }
          },

          /**
           * Starts a drag operation only when it initiates from a gripper control,
           * this is achieved by setting the event index when a gripper is pressed
           */
          onEventStepDragStart: (ev: DragEvent, fromIndex: number): void => {
            if (this.state.draggingGripperIndex !== fromIndex) {
              return ev.preventDefault();
            }

            ev.dataTransfer.setData(`text/plain`, fromIndex.toString());
            ev.dataTransfer.effectAllowed = `move`;
          },

          onEventStepDragEnter: (index: number): void => {
            this.update({draggingOverStepIndex: index});
          },

          onEventStepDragOver: (ev: DragEvent, index: number): void => {
            ev.preventDefault();
            ev.dataTransfer.dropEffect = `move`;
            this.update({draggingOverStepIndex: index, dropPosition: this.getDropPosition(ev)});
          },

          onEventStepDragClear: (): void => this.update({draggingOverStepIndex: null, dropPosition: null}),

          onEventStepDrop: (ev: DragEvent, dropIndex: number): void => {
            ev.preventDefault();
            ev.stopPropagation();
            const fromIndex = parseInt(ev.dataTransfer.getData(`text/plain`), 10);
            let toIndex = dropIndex;
            // Insert after the drop target index if dropping below
            if (this.getDropPosition(ev) === DROP_POSITION.BELOW) {
              toIndex++;
            }
            // Adjust the index to account for removing the item itself if we are moving it down the list
            if (toIndex > fromIndex) {
              toIndex--;
            }
            this.handleEventStepDrop(fromIndex, toIndex);
          },

          onEventSelected: (ev: CustomEvent, index, isPlaceholder): void => {
            const {event, type, property} = ev.detail;
            const action = isPlaceholder ? Action.Add : Action.Edit;
            const entry = !isPlaceholder ? ev.detail : this.getNewEntry(type, event, undefined, property);

            this.updateEventEntries(action, {entry, index});
          },

          onEventIndexLabelClicked: (_, index) => {
            const eventAtIndex = this.attr(`row-index-toggleable`) && this.getEventEntries()[index];
            if (eventAtIndex) {
              const entry: QueryEntry = {
                ...eventAtIndex,
                eventIndexLabelToggled: !eventAtIndex.eventIndexLabelToggled,
              };
              this.updateEventEntries(Action.Edit, {entry, index});
              this.dispatchTrack(TrackingEvent.EventIndexLabelClicked, {
                'Event index': index,
                'Event name': eventAtIndex ? eventAtIndex.event.label : null,
                'Previous Label': this.helpers.getRowLabel(index),
              });
            }
          },

          onGripperMouseDown: (index: number): void => {
            this.update({draggingGripperIndex: index});
          },

          onGripperMouseLeave: (): void => {
            this.update({draggingGripperIndex: null});
          },

          onDateRangeChange: (ev: CustomEvent, index: number) => {
            const entryAtIndex = this.getEventEntries()[index];
            if (this.helpers.isDateRangeType(entryAtIndex.type)) {
              const entry = {
                ...entryAtIndex,
                dateRange: ev.detail as DateRange,
              };
              this.updateEventEntries(Action.Edit, {entry, index});
              this.dispatchTrack(this.getTrackingEvent(entryAtIndex.type, Action.SelectDateRange), {
                ...ev.detail.props,
                'Event index': index,
              });
            }
          },

          getDateRangeAttributes: (type, dateRange) => {
            const commonAttrs = {
              type: `time`,
              'data-key': `time`,
              'display-context': this.attr(`time-display-context`),
              'max-data-history-days': this.attr(`max-data-history-days`),
              'workspace-id': `number`,
            };
            switch (type) {
              case SelectorTypeDefinitions.Behavior:
                return {
                  ...commonAttrs,
                  'input-only': true,
                  label: dateRange ? this.helpers.getDateRangeLabel(dateRange) : `Select date range`,
                  selected: dateRange ? JSON.stringify(dateRange) : ``,
                  'selector-type': `button`,
                  theme: `tertiary`,
                };
              case SelectorTypeDefinitions.EventLaunch:
                return {
                  ...commonAttrs,
                  selected: dateRange ? JSON.stringify(dateRange) : this.attr(`date-range`),
                  'selector-icon': `calendar`,
                  theme: `builder`,
                };
              default:
                throw new Error(`${type} should not have a date range`);
            }
          },

          isDateRangeType: (type) =>
            type === SelectorTypeDefinitions.Behavior || type === SelectorTypeDefinitions.EventLaunch,

          /** Intercepts track events from mp-query-event-selector to add source data */
          trackEventSelectorEvent: (ev: CustomEvent, index: number, isPlaceholder: boolean) => {
            preventTracking(ev);

            if (isPlaceholder || !ev.cancelable) {
              return;
            }

            const type = this.helpers.getEventEntries()[index].type;
            const {eventName, props} = ev.detail;
            let trackingAction = null;

            switch (eventName) {
              case `Add property filter`:
                trackingAction = Action.AddFilter;
                break;
              case `Edit property filter`:
                trackingAction = Action.EditFilter;
                break;
              case `Remove property filter`:
                trackingAction = Action.RemoveFilter;
                break;
              case `Select date range`:
                trackingAction = Action.SelectDateRange;
                break;
              case `Select Event`:
              case `Select Property`:
                trackingAction = Action.Select;
                break;
              default:
                throw new Error(`tracking not defined for event selector event: ${eventName}`);
            }

            this.dispatchTrack(this.getTrackingEvent(type, trackingAction), {
              ...props,
              'Event index': index,
            });
          },

          /** Returns selected math function for a given entry */
          getAggregatorFunctionLabelForEntry: (index: number) => {
            const eventEntry = this.getEventEntries()[index];
            const aggregationOperator = eventEntry.aggregationOperator || PRIMATIVE_MATH_TYPES.TOTAL;
            const mathLabels = eventEntry.property ? PROPERTY_MATH_LABELS : EVENT_MATH_LABELS;

            return mathLabels[findKey(ALL_MATH_TYPES, (key) => key === aggregationOperator)];
          },

          /** Returns list of math function available for a given entry */
          getAggregatorFunctionsForEntry: (index: number) => {
            const entry = this.getEventEntries()[index];
            return this.getAggregatorFunctionsForEntry(entry);
          },

          /**
           * TODO(pablofierro): this needs to go away.
           * Exclusion steps related fns.
           */
          exclusionSteps: {
            getExclusionStep: (index: number) => {
              const entry = this.getEventEntries()[index];
              return {isEntireRange: entry.excludeAll, range: entry.excludeRange};
            },

            getNumExclusionSteps: (): number => {
              return this.attr(`num-exclusion-steps`);
            },

            onExclusionStepSelected: (ev: CustomEvent, index: number) => {
              const {isEntireRange: excludeAll, range: excludeRange}: RangeSelection = ev.detail;

              const entryUpdate: QueryEntry = {
                ...this.getEventEntries()[index],
                excludeAll,
                excludeRange,
              };
              this.updateEventEntries(Action.Edit, {entry: entryUpdate, index});

              this.dispatchTrack(TrackingEvent.EditExclusionStepRange, {
                'Exclude all': excludeAll,
                'Exclude from': excludeRange.from || null,
                'Exclude to': excludeRange.to || null,
              });
            },
          },

          /** Step renaming modal related fns. */
          stepRenameModal: {
            title: () => `Rename Step ${get(this.state, `stepRenameModal.step`, 0) + 1}`,
            close: () => this.update({stepRenameModal: null}),
            text: () => get(this.state, `stepRenameModal.text`, ``),
            onTriggered: (index: number) => {
              const event = this.getEventEntries()[index].event;
              this.update({
                stepRenameModal: {
                  ...(this.state.stepRenameModal || {}),
                  step: index,
                  text: (event as EventItem).renamed || event.label,
                },
              });
            },
            onChangeInput: (ev) => {
              ev.stopPropagation();
              this.update({
                stepRenameModal: {
                  ...(this.state.stepRenameModal || {}),
                  text: ev.target.value,
                },
              });
            },
            onInputElementInserted: (vnode) =>
              requestAnimationFrame(() => {
                vnode.elm.focus();
                vnode.elm.select();
              }),
            onKeyStroke: (ev) => {
              if (ev.keyCode === NAVIGATION_KEY_CODES.ENTER) {
                this.helpers.stepRenameModal.submit();
              }
            },
            submit: () => {
              const index = this.state.stepRenameModal.step;

              const eventAtIndex = this.getEventEntries()[index] as QueryEventEntry;
              const newLabel = this.state.stepRenameModal.text;
              const hasLabel = eventAtIndex.event.label !== newLabel && newLabel !== ``;
              const entry: QueryEntry = {
                ...eventAtIndex,
                event: {
                  ...omit(eventAtIndex.event, [`renamed`]),
                  ...(hasLabel ? {renamed: newLabel} : {}),
                },
              };

              this.updateEventEntries(Action.Edit, {entry, index});
              this.dispatchTrack(TrackingEvent.RenameEvent, {
                'Event index': index,
                'Event name': entry ? entry.event.label : null,
                'is custom event': entry ? entry.event.custom : null,
                'Renamed name': newLabel,
                'is removing rename': !hasLabel,
              });

              this.helpers.stepRenameModal.close();
            },
          },

          getDateRangeLabel: (dateRange: any) => new DateRange(dateRange).getDisplayText(),
        },
      };
    }
    // this has the same effect as this.selectionObject in query-builder. Read there for context
    private stagingEvents: QueryEntry[];

    private dispatchUpdate: Function;
    constructor() {
      super();
      this.dispatchUpdate = debounce((ev) => this._dispatchUpdate(ev), 100);
    }

    getSelectorTypesIcon(entry: QueryEntry) {
      if (entry.type === ResourceType.Event && entry.event.custom) {
        return `custom`;
      }
      if (entry.type === SelectorTypeDefinitions.EventOrUserProperty) {
        if (entry.property && entry.property.resourceType === ResourceType.Event) {
          return `event-attribute`;
        } else {
          return `profile-attribute`;
        }
      }
      return SelectorTypesIcons[entry.type];
    }

    public attributeChangedCallback(attr, oldValue, newValue) {
      super.attributeChangedCallback(attr, oldValue, newValue);

      if (attr === `row-types`) {
        this.abortRowStage();
      } else if (attr === `event-entries`) {
        this.update({placeholders: new Map()});
        this.stagingEvents = this.attr(`event-entries`);
      }
    }

    public getDropPosition(ev: DragEvent) {
      const dropTarget = ev.currentTarget as HTMLElement;
      const rect = dropTarget.getBoundingClientRect();
      return ev.clientY - (rect.top + rect.height / 2) < 0 ? DROP_POSITION.ABOVE : DROP_POSITION.BELOW;
    }

    /**
     * Shifts the step at fromIndex to toIndex,
     * then triggers an event update to requery the data
     */
    public handleEventStepDrop(fromIndex: number, toIndex: number): void {
      if (fromIndex !== toIndex) {
        let entries = this.getEventEntries();
        const entry = cloneDeep(entries[fromIndex]);
        entries = removeByIndex(entries, fromIndex);
        entries = insertAtIndex(entries, toIndex, entry);
        this.emitUpdateEvents(entries);

        this.dispatchTrack(this.getTrackingEvent(entry.type, Action.Drag), {
          'Event name': entry.event.label,
          'From index': fromIndex,
          'To index': toIndex,
        });
      }
    }

    public getEventEntries({appendPlaceholder = false}: {appendPlaceholder?: boolean} = {}): QueryEntry[] {
      let placeHolderType = this.state.stagedType;

      // handle single row limits by always showing the placeholder row
      if (this.attr(`row-limit`) === 1 && this.stagingEvents.length === 0) {
        placeHolderType = this.helpers.getMainActionItem().value;
      }

      return [
        ...this.stagingEvents.map((entry) => {
          const type = entry.type || ResourceType.Event;

          return {
            ...entry,
            // TODO: User entry support is limited to one selection, this could
            // change so that an option per data group is available
            ...(type === ResourceType.User ? {readOnlyEvent: true} : {}),
            type,
          };
        }),
        // placeholder entry
        ...(appendPlaceholder && placeHolderType ? [this.getNewEntry(placeHolderType)] : []),
      ];
    }

    /**
     * Generates a new entry with a default operator applied
     */
    public getNewEntry(
      type: SelectorType,
      event?: EventItem,
      dataGroupId?: string,
      property?: PropertyItem,
    ): QueryEntry {
      const placeHolderEntry = this.getPlaceHolderEventEntry(type);
      return {
        ...placeHolderEntry,
        ...(dataGroupId ? {dataGroupId} : null),
        ...(property ? {property} : null),
        aggregationOperator: this.getEntryDefaultOperator(type),
        event: event || placeHolderEntry.event,
        type,
      } as QueryEntry;
    }

    public getEntryDefaultOperator(type: SelectorType) {
      return type === ResourceType.Cohort ? PRIMATIVE_MATH_TYPES.UNIQUE : PRIMATIVE_MATH_TYPES.TOTAL;
    }

    /**
     * Returns an empty event entry for a given source
     */
    public getPlaceHolderEventEntry(type: SelectorType): QueryEntry {
      const placeholder = EMPTY_EVENT_ENTRY;
      const label = this.attr(`add-entry-label`) || `Select ${this.getPlaceHolderSelectorTypeLabel(type)}`;
      const forExclusionStep = this.attr(`entry-type`) === EntryType.ExclusionEvent;

      return {
        ...(forExclusionStep ? EMPTY_EXCLUSION_EVENT_ENTRY : EMPTY_EVENT_ENTRY),
        type,
        event: {
          ...placeholder.event,
          label,
        },
      };
    }

    public getAggregatorFunctionsForEntry(entry: QueryEntry) {
      let eventMathLabels: Record<string, any> = EVENT_MATH_LABELS;
      let propertyMathLabels: Record<string, any> = PROPERTY_MATH_LABELS;
      if (this.attr(`event-aggregator-functions`)) {
        eventMathLabels = pickBy(EVENT_MATH_LABELS, (_label, value) => {
          return this.attr(`event-aggregator-functions`).includes(PRIMATIVE_MATH_TYPES[value]);
        });
      }
      if (this.attr(`property-aggregator-functions`)) {
        propertyMathLabels = pickBy(PROPERTY_MATH_LABELS, (_label, value) => {
          return this.attr(`property-aggregator-functions`).includes(PRIMATIVE_MATH_TYPES[value]);
        });
      }

      if (isPropertySelectorType(entry.type)) {
        return [];
      }

      const mathLabels = entry.property ? propertyMathLabels : eventMathLabels;
      const mathFunctionsType =
        entry.property && entry.property.type === PropertyType.Number
          ? SelectorTypeDefinitions.EventOrUserProperty
          : entry.type;
      const customMathFunctions = entry.property
        ? this.attr(`property-aggregator-functions`)
        : this.attr(`event-aggregator-functions`);
      const functions = customMathFunctions || SelectorTypeMathFunctions[mathFunctionsType];

      const items = functions.map((value) => ({
        value,
        label: mathLabels[findKey(ALL_MATH_TYPES, (mathType) => mathType === value)],
      }));

      // TODO (safdar): Remove this when behaviors support more aggregator functions
      const minItems = entry.type === SelectorTypeDefinitions.Behavior ? 0 : 1;

      return items.length > minItems ? [{items}] : [];
    }

    public updateEventEntries(action: Action, {entry, index}: {entry?: QueryEntry; index: number}): void {
      let entries = this.getEventEntries();

      switch (action) {
        case Action.Add:
          if (this.attr(`data-group-id`)) {
            entry = this.getNewEntry(entry.type, entry.event, this.attr(`data-group-id`));
          }
          entries = [...entries, entry];
          break;
        case Action.Clone:
          entry = cloneDeep(entries[index]);
          entries = insertAtIndex(entries, index, entry);
          break;
        case Action.Edit:
          entries = replaceByIndex(entries, index, {...entries[index], ...entry});
          break;
        case Action.Remove:
          entry = entries[index];
          entries = removeByIndex(entries, index);
          break;
      }

      this.emitUpdateEvents(entries);
      this.abortRowStage();

      // edit actions are tracked with more detailed elsewhere
      if (action !== Action.Edit) {
        this.dispatchTrack(this.getTrackingEvent(entry.type, action), {
          'Event index': index,
          'Event name': entry ? entry.event.label : null,
          'is custom event': entry ? (entry as QueryEventEntry).event.custom : null,
          ...(entry.type === ResourceType.Cohort
            ? {
                'cohort type': isInlineCohortItem(entry.event) ? `inline cohorts` : `cohorts`,
              }
            : {}),
        });
      }
    }

    public dispatchTrack(eventName: string, props: object): void {
      const properties = {
        ...props,
        ...this.attr(`track-properties`),
      };
      emitTrackEvent(this, {eventName, props: properties, eventPrefix: null, cancelable: true});
    }

    public emitUpdateEvents(eventEntries): void {
      this.stagingEvents = eventEntries;
      const detail = {eventEntries};
      this.dispatchUpdate(new CustomEvent(`update`, {detail}));
    }

    private getPlaceHolderSelectorTypeLabel(type: SelectorType): string {
      return this.helpers.isDateRangeType(type) ? SelectorTypesLabels[ResourceType.Event] : SelectorTypesLabels[type];
    }

    private _dispatchUpdate(ev) {
      this.dispatchEvent(ev);
    }

    private abortRowStage() {
      this.update({
        stagedType: null,
        activeEvent: null,
        activeEventPart: null,
      });
    }

    private isTypeOptionComplex(option: any): option is RowTypeComplexOption {
      return isObject(option);
    }

    /**
     * Generates the type of items that can be added to the list
     */
    private getListTypeItems(): TypeList {
      const sections = {};
      const types = this.attr(`row-types`) as RowTypeOption[];
      const isUserDisabled = !!this.attr(`data-group-id`) && !this.attr(`support-inline-cohorts`);

      // populate selected items
      for (const type of types) {
        if (this.isTypeOptionComplex(type)) {
          sections[type.value] = {
            ...LIST_TYPES[type.value],
            ...type,
          };
        } else {
          sections[type] = LIST_TYPES[type];
        }
      }

      const transforms = {
        [ResourceType.Cohort]: (item) => {
          return {
            ...item,
            isDisabled: isUserDisabled,
            tooltip: isUserDisabled ? {text: `Cohort comparison is disabled when not analyzing users.`} : null,
          };
        },
        [ResourceType.Event]: (item) => {
          return {
            ...item,
            ...(this.attr(`add-entry-label`) ? {label: this.attr(`add-entry-label`)} : {}),
          };
        },
      };

      return mapValues(sections, (value, key) => (transforms[key] ? transforms[key](value) : value));
    }

    private getTrackingEvent(type: ResourceTrackingTypes, action: Action): string {
      const forExclusionStep = this.attr(`entry-type`) === EntryType.ExclusionEvent;

      if (forExclusionStep) {
        type = EntryType.ExclusionEvent;
      }

      return ResourceTrackingEvent[type][action];
    }
  },
);
